'use client';

import { useEffect, useRef, useState } from 'react';
import { SpinnerGap } from '@phosphor-icons/react';
import { ToolVisualizationRenderer } from '@/components/tool-visualization-renderer';
import { useProductTelemetryOnce } from '@/hooks/use-product-telemetry-once';
import { captureProductTelemetry } from '@/lib/product-telemetry';

interface SavedVisualizationPreviewProps {
  visualizationId: string;
  sessionId?: string | null;
  onRetryError?: (info: { toolName: string; error: string; code: string }) => void;
}

interface VisualizationResponse {
  id: string;
  type: string;
  title: string;
  data: Record<string, unknown>;
}

export function SavedVisualizationPreview({
  visualizationId,
  sessionId,
  onRetryError,
}: SavedVisualizationPreviewProps) {
  const [visualization, setVisualization] = useState<VisualizationResponse | null>(null);
  const [error, setError] = useState<string | null>(null);
  const loadStartRef = useRef(typeof performance !== 'undefined' ? performance.now() : Date.now());

  useProductTelemetryOnce(
    'saved_visualization_preview_failed',
    Boolean(error),
    {
      visualization_id: visualizationId,
      failure_kind: 'saved-preview-load-failed',
    },
    error ? `${visualizationId}:${error}` : undefined,
  );

  useProductTelemetryOnce(
    'saved_visualization_preview_loaded',
    Boolean(visualization),
    {
      visualization_id: visualizationId,
      tool_name: visualization?.type,
      load_duration_ms: Math.round((typeof performance !== 'undefined' ? performance.now() : Date.now()) - loadStartRef.current),
    },
    visualization ? `${visualizationId}:${visualization.type}` : undefined,
  );

  useEffect(() => {
    let cancelled = false;

    async function loadVisualization() {
      try {
        setError(null);
        setVisualization(null);
        loadStartRef.current = typeof performance !== 'undefined' ? performance.now() : Date.now();
        const response = await fetch(`/api/visualizations/${visualizationId}`);
        if (!response.ok) {
          const data = await response.json().catch(() => ({}));
          throw new Error(data.error || 'Failed to load visualization');
        }

        const data = await response.json() as VisualizationResponse;
        if (!cancelled) {
          setVisualization(data);
        }
      } catch (loadError) {
        if (!cancelled) {
          setError(loadError instanceof Error ? loadError.message : 'Failed to load visualization');
        }
      }
    }

    void loadVisualization();

    return () => {
      cancelled = true;
    };
  }, [visualizationId]);

  if (error) {
    return (
      <div
        className="rounded-xl border border-destructive/30 bg-destructive/5 p-4 text-sm text-destructive"
        role="alert"
        aria-live="polite"
      >
        {error}
      </div>
    );
  }

  if (!visualization) {
    return (
      <div className="flex items-center justify-center rounded-xl border border-border bg-muted/20 p-8 text-sm text-muted-foreground">
        <SpinnerGap className="mr-2 size-4 animate-spin" />
        Loading updated visualization...
      </div>
    );
  }

  return (
    <ToolVisualizationRenderer
      toolName={visualization.type}
      input={visualization.data}
      visualizationId={visualization.id}
      sessionId={sessionId}
      onRetryError={onRetryError}
    />
  );
}
